import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { Phone, ArrowDown, ShieldCheck } from "lucide-react";
import { BUSINESS, IMAGES } from "@/data/site";
import { scrollToId } from "@/lib/scroll";
import { SplitLines, EASE } from "@/components/motion/Reveal";
import { INTRO } from "@/components/motion/IntroSplash";

const HeroVisual = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 18 });
  const tx = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), { stiffness: 90, damping: 20 });
  const ty = useSpring(useTransform(my, [-0.5, 0.5], [-14, 14]), { stiffness: 90, damping: 20 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={reset}
      initial={{ opacity: 0, y: 60, rotate: -2 }}
      animate={{ opacity: 1, y: 0, rotate: 0 }}
      transition={{ duration: 1.4, delay: INTRO + 0.5, ease: EASE }}
      style={{ perspective: 1200 }}
      className="relative w-full max-w-md lg:max-w-none mx-auto"
      data-testid="hero-visual"
    >
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative">
        <div className="clip-frame relative aspect-[4/5] overflow-hidden bg-panel">
          <motion.img
            src={IMAGES.tradie}
            alt="Big Rickie on the job in his Big Rickie's Pressure Washing shirt"
            className="absolute inset-0 w-full h-full object-cover object-right scale-105"
            style={{ x: tx, y: ty }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" />
          <div className="absolute left-5 bottom-5 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-300">
            Fig. 01 — Rickie on the tools, Bendigo
          </div>
        </div>
        <div className="absolute -inset-3 border border-brand/30 clip-frame pointer-events-none" style={{ transform: "translateZ(-40px)" }} />

        <motion.div
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: INTRO + 1.3, duration: 0.9, ease: EASE }}
          style={{ transform: "translateZ(60px)" }}
          className="absolute -left-4 sm:-left-10 bottom-16 bg-ink/90 backdrop-blur-xl border border-white/10 rounded-lg p-4 pr-6 shadow-2xl"
          data-testid="hero-float-card"
        >
          <div className="eyebrow !text-cyanglow mb-1">Response time</div>
          <div className="font-display font-black text-4xl text-white leading-none">
            &lt;24<span className="text-brand-light">h</span>
          </div>
          <div className="text-xs text-slate-400 mt-1">Quotes usually same day</div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: INTRO + 1.5, duration: 0.9, ease: EASE }}
          style={{ transform: "translateZ(80px)" }}
          className="absolute -right-3 sm:-right-8 top-8 bg-brand text-white rounded-lg px-4 py-3 shadow-[0_20px_50px_rgba(27,114,217,0.35)] rotate-3"
        >
          <div className="flex items-center gap-1.5 font-display font-black text-2xl leading-none">
            <ShieldCheck size={22} strokeWidth={2.5} /> INSURED
          </div>
          <div className="font-mono text-[10px] uppercase tracking-widest mt-1">Public liability covered</div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden pt-40 pb-20 lg:pt-44">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 -z-10">
        <img src={IMAGES.hero} alt="" className="w-full h-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/90 to-ink/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/60" />
      </motion.div>
      <div className="absolute inset-0 -z-10 blueprint opacity-60" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 w-full grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <motion.div style={{ y: textY, opacity: fade }} className="lg:col-span-7">

          <p className="eyebrow mb-5">
            <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: INTRO + 0.5 }}>
              Pressure washing &amp; external cleaning — Bendigo, VIC
            </motion.span>
          </p>

          <h1 className="h-display text-[3.4rem] sm:text-7xl lg:text-[6.5rem] xl:text-[7.5rem] text-white" data-testid="hero-heading">
            <SplitLines onMount delay={INTRO + 0.55} lines={["Bendigo's", "dirtiest jobs.", <>Done <span className="text-brand-light">bloody</span></>, "properly."]} />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: INTRO + 1.1, duration: 0.9, ease: EASE }}
            className="mt-8 max-w-xl text-slate-300 text-base sm:text-lg leading-relaxed"
          >
            Driveways, houses, roofs, gutters, windows and solar panels — cleaned by a local who actually cares how it turns out. Commercial-grade gear, fixed quotes, and a 5.0 rating to back it up.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: INTRO + 1.25, duration: 0.9, ease: EASE }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <a href={`tel:${BUSINESS.phoneRaw}`} data-testid="hero-call-btn" className="btn-primary">
              <Phone size={20} strokeWidth={2.5} /> Call {BUSINESS.phone}
            </a>
            <button onClick={() => scrollToId("quote")} data-testid="hero-get-quote-btn" className="btn-ghost">
              Get a free quote <ArrowDown size={18} />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: INTRO + 1.6, duration: 1 }}
            className="mt-12 flex flex-wrap gap-x-8 gap-y-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500"
          >
            <span>Fully insured</span>
            <span>Fixed-price quotes</span>
            <span>Residential + commercial</span>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5 lg:pl-6">
          <HeroVisual />
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: INTRO + 2 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-2 text-slate-500"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }} className="w-px h-8 bg-gradient-to-b from-brand to-transparent" />
      </motion.div>
    </section>
  );
};
